/* eslint-disable react/jsx-no-bind */
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@concur/nui-widgets/Dialog';
import Button from '@concur/nui-widgets/Button';
import IconButton from '@concur/nui-widgets/IconButton';
import classnamesBind from 'classnames/bind';
import { withFormatter } from '@concur/nui-intl-runtime';
import Logo from '../Logo/Logo';
import LastLogin from './_LastLogin';
import styles from './_AboutDialog.less';

const CSS_BLOCK = 'sapcnqr-about-dialog';
const year = new Date().getFullYear();

const AboutDialog = ({
    show,
    formatter,
    partnerLogo,
    lastSigninDateString,
    isProfessional,
    isStandard,
    showAboutDialogCallback,
    isOpusUser,
}) => {
    const backButtonRef = useRef(null);
    const closeButtonRef = useRef(null);
    const lastSigninButtonRef = useRef(null);

    const classnames = classnamesBind.bind(styles);
    const [showDialog, setShowDialog] = useState(show);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const indexToFocusOnReturn = useRef(0);

    useEffect(() => {
        setShowDialog(show);
    }, [show]);

    const backButton = (
        <IconButton
            ariaLabel={formatter.formattedMessage({ id: 'CoreUI.errorPage_Error.back' })}
            iconName="slim-arrow-left"
            ref={backButtonRef}
            onClick={() => {
                setSelectedIndex(0);
                indexToFocusOnReturn.current = selectedIndex;
            }}
        />
    );

    return (
        <Dialog
            data-test="about-dialog"
            firstFocusRef={selectedIndex === 0
                ? lastSigninButtonRef || closeButtonRef : backButtonRef}
            footer={{
                ariaLabel: formatter.formattedMessage({ id: 'CoreUI.about.toolbar' }),
                mainContent: [
                    <Button type="secondary" key="btn1" data-test="about-dialog-close" onClick={() => showAboutDialogCallback(false)} ref={closeButtonRef}>
                        {formatter.formattedMessage({ id: 'CoreUI.close' })}
                    </Button>,
                ],
            }}
            bodyProps={{
                className: classnames(`${CSS_BLOCK}__body`),
            }}
            dimensions={{
                minHeight: '100px',
            }}
            onHide={() => showAboutDialogCallback(false)}
            show={showDialog}
            title={selectedIndex === 0 ? formatter.formattedMessage({ id: 'CoreUI.about.title' }) : formatter.formattedMessage({ id: 'CoreUI.loginHistory' })}
            additionalTitleContentStart={selectedIndex !== 0 && backButton}
        >
            {selectedIndex === 0 && (
                <>
                    {!partnerLogo && (
                        <Logo
                            className={classnames(`${CSS_BLOCK}__home-icon`)}
                            cssBlock={CSS_BLOCK}
                            isOpusUser={isOpusUser}
                            styles={styles}
                        />
                    )}
                    {/* eslint-disable-next-line react/no-danger */}
                    {partnerLogo && <div data-test="partner-logo" dangerouslySetInnerHTML={{ __html: partnerLogo }} />}
                    {(isProfessional || isStandard) && (
                        <span className={classnames(`${CSS_BLOCK}__container`)}>
                            <span className={classnames(`${CSS_BLOCK}__label`)}>
                                {formatter.formattedMessage({ id: 'CoreUI.about.edition' })}
                            </span>
                            <span className={classnames(`${CSS_BLOCK}__value`)}>
                                {isProfessional ? formatter.formattedMessage({ id: 'CoreUI.about.professional' }) : formatter.formattedMessage({ id: 'CoreUI.about.standard' })}
                            </span>
                        </span>
                    )}
                    {lastSigninDateString && (
                        <Button
                            data-analytics-json={'{"applicationName":"userprofilemenu","elementPath":"last-signed-in","action":"click","elementType":"link"}'}
                            onClick={() => setSelectedIndex(1)}
                            type="link"
                            ref={lastSigninButtonRef}
                            data-test="last-sign-in-button"
                        >
                            {formatter.formattedMessage({ id: 'CoreUI.lastSignIn.info', values: { dateAndTime: lastSigninDateString } })}
                        </Button>
                    )}
                    <span className={classnames(`${CSS_BLOCK}__copyright`)}>
                        {isOpusUser
                            ? formatter.formattedMessage({ id: 'CoreUI.opusCopyright', values: { year } })
                            : formatter.formattedMessage({ id: 'CoreUI.copyright', values: { year } })}
                    </span>
                </>
            )}
            {selectedIndex !== 0 && (
                <LastLogin
                    styles={styles}
                    cssBlock={CSS_BLOCK}
                />
            )}
        </Dialog>
    );
};

AboutDialog.displayName = 'AboutDialog';

AboutDialog.propTypes = {
    show: PropTypes.bool,
    lastSigninDateString: PropTypes.string,
    partnerLogo: PropTypes.string,
    isProfessional: PropTypes.bool,
    isStandard: PropTypes.bool,
    showAboutDialogCallback: PropTypes.func,
    isOpusUser: PropTypes.bool,
};

export default withFormatter(AboutDialog);
